<template>
  <div class="top-navigation-bar" :style="navBarStyle">
    <cst-menu></cst-menu>
    <div class="container">
      <div class="header-information">
        <div class="navigation-section">
          <back-to-dashboard-link :type="dashboardType.changeOrder" />
        </div>
        <div class="info-data">
          <div
            v-if="hasQuoteId"
            class="info-item clickable"
            @click="openQuote(changeOrder.QuoteId)"
          >
            <label>
              {{ i18n.t('/configuration/quoteId') }}
            </label>
            <span>
              {{ changeOrder.QuoteId }}
            </span>
          </div>
          <div class="info-item">
            <label>
              {{ i18n.t('/configuration/komnumber') }}
            </label>
            <span>{{ changeOrder.KomNumber }}</span>
          </div>
          <div class="info-item">
            <label>
              {{ i18n.t('/change-order/navigation/model-type') }}
            </label>
            <span>{{ getModelName }}</span>
          </div>
          <div class="info-item">
            <label>
              {{ i18n.t('/change-order/navigation/dealer') }}
            </label>
            <span>{{ getOwningDealerId }}</span>
          </div>
          <div class="info-item">
            <label>
              {{ i18n.t('/change-order/navigation/logistic-status') }}
            </label>
            <span>{{ changeOrder.FactoryStatusDescription }}</span>
          </div>
          <div v-if="dealerSettings.isImporter" class="info-item">
            <label>
              {{ i18n.t('/change-order/navigation/total-price') }}
            </label>
            <span>{{ getFormattedCarInclPrice }}</span>
          </div>
        </div>
      </div>
      <nav class="navigation-bar">
        <ul class="nav-list">
          <li
            v-for="(section, index) in getSections"
            :key="index"
            class="nav-item"
            @click="scrollToSection(section)"
          >
            {{ section.name }}
            <i v-if="!section.allowed" :title="section.reason" class="fas fa-lock"></i>
          </li>
        </ul>
      </nav>
    </div>
    <div v-if="changeOrder?.AudiExclusiveNotice === 'INFO'" class="audi-exclusive-info">
      <div class="container">
        <alert type="warning" :text="i18n.t('/change-order/audi-exclusive/info')" />
      </div>
    </div>
  </div>
</template>
<script>
import BackToDashboardLink from '@/components/generic/presentational/molecules/backToDashboardLink/BackToDashboardLink.vue'
import dashboardType from '@/helpers/enums/dashboardType'
import { mapGetters } from 'vuex'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import CstMenu from '@/components/generic/presentational/molecules/cstMenu/CstMenu.vue'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'

export default {
  components: {
    BackToDashboardLink,
    CstMenu,
    Alert,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      dashboardType: dashboardType,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
      sidebarOpen: 'ui/sidebarOpen',
      sidebarWidth: 'ui/sidebarWidth',
      doNotDeliverBeforeEditEnabled: 'user/doNotDeliverBeforeEditEnabled',
    }),
    navBarStyle() {
      return {
        left: this.sidebarOpen ? `${this.sidebarWidth}px` : '0',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    },
    hasQuoteId() {
      return this.changeOrder.QuoteId
    },
    getSections() {
      let sections = [
        {
          name: this.i18n.t('/change-order/sections/configuration'),
          refId: 'configSection',
          visible: true, //always display this
          allowed: this.canChangeCarConfiguration,
          reason: this.canChangeCarConfigurationReason,
        },
        {
          name: this.i18n.t('/change-order/sections/control-codes'),
          refId: 'importerOptionsSection',
          visible: true, //always display this
          allowed: this.canChangeImporterOptions,
          reason: this.canChangeImporterOptionsReason,
        },
        {
          name: this.i18n.t('/change-order/sections/delivery-dates'),
          refId: 'deliverySection',
          visible: true, //always display this
          allowed: this.canChangeDeliveryDate && this.doNotDeliverBeforeEditEnabled,
          reason: this.buildReason(
            this.canChangeDeliveryDateReason,
            this.doNotDeliverBeforeEditEnabledReason
          ),
        },
        {
          name: this.i18n.t('/change-order/sections/dealer-and-location'),
          refId: 'establishmentSection',
          visible: !this.hasQuoteId,
          allowed: this.canChangeEstablishmentOrLoadingLocation,
          reason: this.canChangeEstablishmentOrLoadingLocationReason,
        },
        {
          name: this.i18n.t('/change-order/sections/customer-data'),
          refId: 'customerSection',
          visible: !this.hasQuoteId,
          allowed: this.canChangeCustomer,
          reason: this.canChangeCustomerReason,
        },
        {
          name: this.i18n.t('/change-order/sections/reservation'),
          refId: 'reservationSection',
          visible: !this.hasQuoteId,
          allowed: this.canChangeReservation,
          reason: this.canChangeReservationReason,
        },
        {
          name: this.i18n.t('/change-order/sections/license-plate'),
          refId: 'licensePlateSection',
          visible: this.dealerSettings.isImporter,
          allowed: this.canChangeLicensePlate,
          reason: this.canChangeLicensePlateReason,
        },
        {
          name: this.i18n.t('/common/cancel'),
          refId: 'deleteOrderSection',
          visible: this.dealerSettings.isImporter,
          allowed: this.canDeleteOrder,
          reason: this.canDeleteOrderReason,
        },
      ]

      return sections.filter((s) => s.visible)
    },
    getMainOrderStatus() {
      if (this.changeOrder?.MainOrderStatus === ChangeOrderStatus.submitted) {
        return this.i18n.t('/change-order/status/submitted')
      }
      if (this.changeOrder?.MainOrderStatus === ChangeOrderStatus.archived) {
        return this.i18n.t('/change-order/status/archived')
      }

      return null
    },
    getOwningDealerId() {
      return this.changeOrder.OwningDealer?.OriginalPartnerNumber
    },
    getModelName() {
      let modelName = this.changeOrder.CarConfiguration.ModelName
      let trimlineName = this.changeOrder.CarConfiguration.TrimlineName
      let version = modelName + ' ' + trimlineName
      if (version.length > 20) {
        return version.substring(0, 20) + '...'
      }
      return version
    },
    getFormattedCarInclPrice() {
      const formatter = new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      })
      let carInclPrice = this.changeOrder.CarConfiguration.ChangedNetCatalogPrice.Incl
      return formatter.format(carInclPrice)
    },
    canChangeCarConfiguration() {
      let permissions = this.changeOrder?.Permissions
      return (
        (permissions?.CanChangeColor?.Allowed === true ||
          permissions?.CanChangeInterior?.Allowed === true ||
          permissions?.CanChangeFactoryOption?.Allowed === true) &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeCarConfigurationReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(
        permissions?.CanChangeColor?.Reason,
        permissions?.CanChangeInterior?.Reason,
        permissions?.CanChangeFactoryOption?.Reason,
        this.getMainOrderStatus
      )
    },
    canChangeImporterOptions() {
      let permissions = this.changeOrder?.Permissions
      return (
        this.dealerSettings.isImporter &&
        permissions?.CanChangeImporterOptions?.Allowed === true &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeImporterOptionsReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(
        permissions?.CanChangeImporterOptions?.Reason,
        this.getMainOrderStatus
      )
    },
    canChangeDeliveryDate() {
      let permissions = this.changeOrder?.Permissions
      return (
        permissions?.CanChangeDeliveryDates?.Allowed === true &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeDeliveryDateReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(permissions?.CanChangeDeliveryDates?.Reason, this.getMainOrderStatus)
    },
    doNotDeliverBeforeEditEnabledReason() {
      if (!this.doNotDeliverBeforeEditEnabled) {
        return this.i18n.t('/change-order/delivery-dates/dealer-restriction-locked')
      }
      return null
    },
    canChangeEstablishmentOrLoadingLocation() {
      let permissions = this.changeOrder?.Permissions
      return (
        (permissions?.CanChangeOwningDealer?.Allowed === true ||
          permissions?.CanChangeUnloadingLocation?.Allowed === true) &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeEstablishmentOrLoadingLocationReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(
        permissions?.CanChangeOwningDealer?.Reason,
        permissions?.CanChangeUnloadingLocation?.Reason,
        this.getMainOrderStatus
      )
    },
    canChangeCustomer() {
      let permissions = this.changeOrder?.Permissions
      return (
        this.dealerSettings.isImporter &&
        permissions?.CanChangeCustomer?.Allowed === true &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeCustomerReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(permissions?.CanChangeCustomer?.Reason, this.getMainOrderStatus)
    },
    canChangeReservation() {
      let permissions = this.changeOrder?.Permissions
      return (
        this.dealerSettings.isImporter &&
        (permissions?.CanChangeReservations?.Allowed === true ||
          permissions?.CanChangeHoldShowroom?.Allowed === true ||
          permissions?.CanChangeHoldStock?.Allowed === true) &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeReservationReason() {
      let permissions = this.changeOrder?.Permissions
      return this.buildReason(
        permissions?.CanChangeReservations?.Reason,
        permissions?.CanChangeHoldShowroom?.Reason,
        permissions?.CanChangeHoldStock?.Reason,
        this.getMainOrderStatus
      )
    },
    canChangeLicensePlate() {
      let permissions = this.changeOrder.Permissions
      let canChangeHoldCommercial = permissions?.CanChangeHoldCommercial
      let canChangeSettlement = permissions?.CanChangeSettlement
      let canChangeLicensePlate = permissions?.CanChangeLicensePlate
      return (
        (canChangeSettlement?.Allowed === true ||
          canChangeHoldCommercial?.Allowed === true ||
          canChangeLicensePlate?.Allowed === true) &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    canChangeLicensePlateReason() {
      return this.buildReason(
        this.changeOrder?.Permissions?.CanChangeHoldCommercial?.Reason,
        this.changeOrder?.Permissions?.CanChangeSettlement?.Reason,
        this.changeOrder?.Permissions?.CanChangeLicensePlate?.Reason,
        this.getMainOrderStatus
      )
    },
    canDeleteOrder() {
      return (
        this.changeOrder?.Permissions?.CanDeleteOrder?.Allowed === true &&
        this.isMainOrderNotSubmittedOrArchived
      )
    },
    isMainOrderNotSubmittedOrArchived() {
      return (
        this.changeOrder?.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder?.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    canDeleteOrderReason() {
      return this.buildReason(
        this.changeOrder?.Permissions?.CanDeleteOrder?.Reason,
        this.getMainOrderStatus
      )
    },
  },
  methods: {
    buildReason(...reasons) {
      return [...new Set(reasons.filter((r) => r != null && r !== '').map((r) => r.trim()))].join(
        '\n'
      )
    },
    openQuote(quoteId) {
      let params = {
        cstType: 'b2c',
        establishmentId: this.$route.params.establishmentId,
        quoteId: quoteId,
      }
      this.$router.push({ name: 'configuration-page', params })
    },
    scrollToSection(section) {
      const offset = 150
      const element = document.getElementById(section.refId)
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - offset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.top-navigation-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 998;
  background: $gray-lightest;
}

.header-information {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.info-data {
  display: flex;
  justify-content: space-between;
  margin-right: 30px;
  width: 900px;
}

.info-item {
  margin-left: 1rem;
  text-align: left;
  font-size: 0.875rem;

  label {
    display: block;
  }

  span {
    font-weight: bold;
    display: block;
  }
}

.clickable {
  cursor: pointer;
}

.clickable * {
  cursor: inherit;
}

.navigation-bar {
  color: $white;
  text-align: center;
  font-size: 0.875rem;
  font-weight: normal;
  margin-top: 20px;
}

.audi-exclusive-info {
  background: $white;
  padding: 15px 0 30px;

  .alert {
    margin: 0;
  }
}

.nav-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: space-around; /* This will evenly space your nav items */
}

.nav-item {
  cursor: pointer;
  padding: 5px 0 5px 0;
  border-right: 1px solid #fff;
  background-color: $color-primary;
  flex: 1;
  text-align: center;

  &:hover {
    background-color: $color-primary-dark; /* Darker shade for hover state */
  }

  &:last-child {
    border-right: none; /* Removes the border for the last item */
  }
}

.container {
  @include center;
  padding-top: 10px;
}

.navigation-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
